/**
 * Pure helpers that turn an agent-set document (as returned by GET
 * /agent-sets/{id}) into a graph model for React Flow: one node per member
 * agent and one edge per inter-agent link (a `transfer_agent` or `subagent`
 * builtin function pointing at another member).
 *
 * Kept free of any rendering / React Flow imports so it is trivially testable.
 */

// builtin platform → edge kind
const AGENT_LINK_PLATFORMS = { transfer_agent: 'transfer', subagent: 'subagent' };

/** Agent functions are stored either as an array or as an object keyed by name. */
function functionList(functions) {
  if (Array.isArray(functions)) return functions;
  if (functions && typeof functions === 'object') return Object.values(functions);
  return [];
}

/**
 * The outbound links (transfers + subagent delegations) declared by one member.
 * @returns {Array<{kind, functionName, targetId, targetLabel}>}
 */
export function memberLinks(agent) {
  return functionList(agent?.functions)
    .filter((f) => f?.implementation === 'builtin' && AGENT_LINK_PLATFORMS[f.platform])
    .map((f) => {
      const param = f.input_schema?.properties?.agent || {};
      const from = param.from;
      const labelFromRef = typeof from === 'string' && from.startsWith('label:')
        ? from.slice('label:'.length)
        : undefined;
      return {
        kind: AGENT_LINK_PLATFORMS[f.platform],
        functionName: f.name,
        // Saved sets carry a resolved UUID + `fromLabel`; an unsaved/draft
        // document may still hold a raw `label:foo` reference.
        targetId: labelFromRef ? undefined : from,
        targetLabel: param.fromLabel ?? labelFromRef,
      };
    });
}

/**
 * The phone-number transfers (`platform: 'transfer'`) declared by one member —
 * a live hand-off to an external number rather than to another agent.
 * @returns {Array<{functionName, number}>} `number` is the literal dialled
 *   number when statically configured, else undefined (resolved per-call from
 *   metadata, so there is nothing fixed to show).
 */
export function memberPhoneTransfers(agent) {
  return functionList(agent?.functions)
    .filter((f) => f?.implementation === 'builtin' && f.platform === 'transfer')
    .map((f) => {
      const param = f.input_schema?.properties?.number || {};
      return {
        functionName: f.name,
        number: param.source === 'static' && typeof param.from === 'string' ? param.from : undefined,
      };
    });
}

function nodeKey(agent) {
  return agent.id || `label:${agent.label}`;
}

/**
 * Build `{ nodes, edges }` (React Flow shapes, positions left at 0,0 for the
 * layout pass) from a set document.
 *
 * Node `data`: `{ label, name, modelName, agentType, isEntry, external }`.
 * Edge `data`: `{ kind: 'transfer'|'subagent', functionName }`.
 *
 * Entry ("main") agents are interactive-audio members that nothing transfers
 * *into* — the agents a caller actually reaches first. External references
 * (targets outside this set) render as faded placeholder nodes.
 */
export function buildSetGraph(set) {
  const agents = Array.isArray(set?.agents) ? set.agents : [];
  const byId = new Map(agents.filter((a) => a.id).map((a) => [a.id, a]));
  const byLabel = new Map(agents.filter((a) => a.label).map((a) => [a.label, a]));

  const nodes = agents.map((a) => ({
    id: nodeKey(a),
    type: 'agentNode',
    position: { x: 0, y: 0 },
    data: {
      label: a.label,
      name: a.name || a.label,
      modelName: a.modelName,
      agentType: a.type || 'interactive-audio',
      isEntry: false,
      external: false,
    },
  }));

  const edges = [];
  const externalNodes = new Map();
  const phoneNodes = new Map();
  const inboundTransfers = new Map(); // nodeId → count of transfer edges into it

  agents.forEach((a) => {
    const sourceId = nodeKey(a);
    // Phone-number transfers: each becomes its own compact endpoint node so it
    // can carry that function's name. Keyed per source+function (a number is an
    // external endpoint, not a shared member, so we don't dedupe across agents).
    memberPhoneTransfers(a).forEach((t, i) => {
      const targetId = `phone:${sourceId}:${i}`;
      phoneNodes.set(targetId, {
        id: targetId,
        type: 'phoneNode',
        position: { x: 0, y: 0 },
        data: { name: t.functionName, number: t.number },
      });
      edges.push({
        id: `${sourceId}--phone--${targetId}`,
        source: sourceId,
        target: targetId,
        label: t.functionName,
        data: { kind: 'phone', functionName: t.functionName, number: t.number },
      });
    });
    memberLinks(a).forEach((link, i) => {
      const member = (link.targetId && byId.get(link.targetId))
        || (link.targetLabel && byLabel.get(link.targetLabel));
      let targetId;
      if (member) {
        targetId = nodeKey(member);
      } else if (link.targetId || link.targetLabel) {
        // Reference to an agent outside this set — show it faded.
        const ref = link.targetId || link.targetLabel;
        targetId = `ext:${ref}`;
        if (!externalNodes.has(targetId)) {
          externalNodes.set(targetId, {
            id: targetId,
            type: 'agentNode',
            position: { x: 0, y: 0 },
            data: {
              label: link.targetLabel || 'external',
              name: link.targetLabel
                || (link.targetId ? `${String(link.targetId).slice(0, 8)}…` : 'external'),
              modelName: null,
              agentType: link.kind === 'subagent' ? 'text' : 'interactive-audio',
              isEntry: false,
              external: true,
            },
          });
        }
      } else {
        return; // empty / unresolved target — nothing to draw
      }

      edges.push({
        id: `${sourceId}--${link.kind}--${targetId}--${i}`,
        source: sourceId,
        target: targetId,
        label: link.functionName,
        data: { kind: link.kind, functionName: link.functionName },
      });

      if (link.kind === 'transfer') {
        inboundTransfers.set(targetId, (inboundTransfers.get(targetId) || 0) + 1);
      }
    });
  });

  nodes.forEach((n) => {
    if (n.data.agentType === 'interactive-audio' && !inboundTransfers.get(n.id)) {
      n.data.isEntry = true;
    }
  });

  return { nodes: [...nodes, ...externalNodes.values(), ...phoneNodes.values()], edges };
}
